import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { APITypes, MedicalDataTypes } from '../../../primary-record-types';
import ResourceListSearchFilters, {
    SearchFilterValue,
    SearchInputRecord,
    useApplySearchFilter,
    applySearchFilter,
    getDefaultSearchFilterValue,
    getFilterSummary,
} from './ResourceListSearchFilters';
import SearchResultsList from './SearchResultsList';
import Button from '../../v2BaseComponents/Button';
import HeaderContainer from '../../v2BaseComponents/HeaderContainer';
import Stack from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { getSpacing } from '../../../utils/spacing';
import EmptyStateHint, { LinkEmptyStateHint } from '../EmptyStateHint';
import { getIntl } from '../../../utils/intl';

const intl = getIntl('ResourceSearch', {
    viewAll: 'View All ({count})',
});

export type ResourceSearchOwnProps = {
    resources: APITypes.medicalDataService.ListResourceResponseItem[];
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    isLoadingResources: boolean;
    renderFilteredResources: (resources: SearchInputRecord) => React.ReactElement;
    disableDetailedSearch?: boolean;
    disableFlatListPadding?: boolean;
    onSearchFilterUpdate?: (searchFilter: SearchFilterValue, count: number) => void;
    linkEmptyStateHint?: boolean;
    emptyStateRenderNoKnownDrugAllergies?: React.ReactNode;
    emptyStateHideDefaultHeader?: boolean;
};

export type ResourceSearchProps = ResourceSearchOwnProps;

const ResourceSearch: React.FC<ResourceSearchProps> = ({
    resources,
    category,
    isLoadingResources,
    renderFilteredResources,
    disableDetailedSearch,
    disableFlatListPadding,
    onSearchFilterUpdate,
    linkEmptyStateHint,
    emptyStateRenderNoKnownDrugAllergies,
    emptyStateHideDefaultHeader,
}) => {
    const defaultSearchFilterValue = useMemo(
        () => getDefaultSearchFilterValue(!disableDetailedSearch),
        [disableDetailedSearch],
    );
    const [searchFilter, setSearchFilterState] = useState<SearchFilterValue>(defaultSearchFilterValue);

    const setSearchFilter = useCallback(
        (state: SearchFilterValue) => {
            setSearchFilterState(state);
        },
        [setSearchFilterState],
    );

    // anytime detailed search is changed, reset the search filter
    useEffect(() => {
        setSearchFilter(getDefaultSearchFilterValue(!disableDetailedSearch));
    }, [disableDetailedSearch, setSearchFilter]);
    const filteredResources = useApplySearchFilter(resources || [], searchFilter, category);

    // notify parent component of search filter changes
    useEffect(() => {
        onSearchFilterUpdate?.(searchFilter, filteredResources.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilter, filteredResources?.length]);

    // Visits: the default window hides far-future entries - surface a one-tap shortcut
    // to the full list (the When filter offers the finer options).
    const beyondWindowCount = useMemo(() => {
        const isDefaultWindow = !searchFilter.visitWindow || searchFilter.visitWindow === 'next-month';
        if (category !== 'visit' || !isDefaultWindow || searchFilter.searchText) return 0;
        return (
            applySearchFilter(resources || [], { ...searchFilter, visitWindow: 'all' }, category).length -
            filteredResources.length
        );
    }, [category, searchFilter, resources, filteredResources.length]);

    // Summary text and the View All shortcut share one row - summary left, button right.
    const renderFilterSummary = () => {
        const filterSummary = disableDetailedSearch
            ? null
            : getFilterSummary({
                  category,
                  value: searchFilter,
                  displayedCount: filteredResources.length,
              });
        if (!filterSummary && !beyondWindowCount) return null;

        return (
            <Stack
                horizontal
                fullWidth
                justify="space-between"
                align="center"
                space={2}
                style={{ paddingBottom: getSpacing(2.5) }}
            >
                <Stack horizontal space={2} justify="flex-start" flex={1}>
                    {filterSummary ? (
                        <>
                            <Text variant="body-1">{filterSummary.pretext}</Text>
                            <Text variant="bold-body-1">{filterSummary.filter}</Text>
                        </>
                    ) : null}
                </Stack>
                {beyondWindowCount ? (
                    <Button
                        variant="primary"
                        size="sm"
                        title={intl('viewAll', { count: beyondWindowCount })}
                        onPress={() => setSearchFilter({ ...searchFilter, visitWindow: 'all' })}
                        testID="visits-view-all-button"
                    />
                ) : null}
            </Stack>
        );
    };

    return (
        <Stack vertical fullWidth flex={1}>
            <HeaderContainer>
                <ResourceListSearchFilters
                    value={searchFilter}
                    onChange={setSearchFilter}
                    category={category}
                    allResources={resources || []}
                    disableDetailedSearch={!!disableDetailedSearch}
                    defaultSearchFilterValue={defaultSearchFilterValue}
                />
            </HeaderContainer>
            {resources.length ? (
                <>
                    <SearchResultsList
                        renderBefore={() => renderFilterSummary() || null}
                        isLoadingResources={isLoadingResources}
                        disableFlatListPadding={disableFlatListPadding}
                        filteredResources={filteredResources}
                        renderFilteredResources={renderFilteredResources}
                    />
                </>
            ) : (
                <Stack vertical fullWidth flex={1}>
                    {linkEmptyStateHint ? (
                        <LinkEmptyStateHint variant={category} />
                    ) : (
                        <EmptyStateHint
                            variant={category}
                            renderNoKnownDrugAllergies={emptyStateRenderNoKnownDrugAllergies}
                            hideDefaultHeader={emptyStateHideDefaultHeader}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default React.memo(ResourceSearch);
